/**
 * Poseidon dashboard — staleness indicators.
 *
 * §13 Phase 1 acceptance gate: the dashboard must "label stale data visibly".
 * These components are the only place freshness is rendered, so the rule cannot
 * be applied inconsistently from page to page.
 */
import { FRESHNESS_THRESHOLDS, SOCKET_POLICY } from '../config';
import { formatAge, formatDateTime, formatDateTimeWithZone } from '../lib/format';
import { cx } from '../lib/cx';
import { computeStaleness, useNow, useStaleness } from '../lib/staleness';
import type { StalenessThresholds } from '../lib/staleness';
import type { DataFreshness } from '../api/types';
import { useDataClock } from '../state/DataClock';
import { useServices } from '../state/Services';

export function StalenessBadge({
  lastAt,
  thresholds,
  label,
}: {
  lastAt: string | null;
  thresholds: StalenessThresholds;
  label?: string;
}): JSX.Element {
  const staleness = useStaleness(lastAt, thresholds);
  const text = label === undefined ? staleness.label : `${label}: ${staleness.label}`;
  return (
    <span
      className={cx('stale-badge', `stale-badge--${staleness.level}`)}
      title={staleness.advice ?? `Data age ${formatAge(staleness.ageMs)}`}
      role={staleness.level === 'stale' ? 'alert' : undefined}
    >
      {staleness.level === 'stale' && <span aria-hidden="true">⚠</span>}
      {text}
      <span className="text-mono">{formatAge(staleness.ageMs)}</span>
    </span>
  );
}

/**
 * The persistent "last data timestamp" the operator checks before trusting
 * anything else on the screen. It reports the newest `as_of` any successful
 * request has produced, plus how long ago that was.
 */
export function LastDataTimestamp(): JSX.Element {
  const { lastDataAt, provenance, mode } = useDataClock();
  const staleness = useStaleness(lastDataAt, {
    aging: FRESHNESS_THRESHOLDS.snapshot_aging_ms,
    stale: FRESHNESS_THRESHOLDS.snapshot_stale_ms,
  });

  return (
    <div className="data-clock">
      <span className="text-faint">Last data</span>
      <span className="text-mono" title={lastDataAt === null ? 'No payload received yet' : formatDateTime(lastDataAt)}>
        {lastDataAt === null ? '—' : formatDateTimeWithZone(lastDataAt)}
      </span>
      <StalenessBadge lastAt={lastDataAt} thresholds={{ aging: FRESHNESS_THRESHOLDS.snapshot_aging_ms, stale: FRESHNESS_THRESHOLDS.snapshot_stale_ms }} />
      <span className="text-faint">
        source <strong className={provenance === 'synthetic' || mode === 'mock' ? 'text-warn' : 'text-ok'}>{provenance ?? mode.toUpperCase()}</strong>
      </span>
      <span className="sr-only">{staleness.advice ?? 'Data is within the freshness window.'}</span>
    </div>
  );
}

/** Per-feed detail: tick age, bar age and clock drift, each judged separately. */
export function FreshnessMeter({ freshness }: { freshness: DataFreshness }): JSX.Element {
  const now = useNow(1_000);
  const tick = computeStaleness(freshness.last_tick_at, { aging: freshness.thresholds.tick_aging_ms, stale: freshness.thresholds.tick_stale_ms }, now);
  const bar = computeStaleness(freshness.last_bar_at, { aging: freshness.thresholds.bar_aging_ms, stale: freshness.thresholds.bar_stale_ms }, now);
  const drift = freshness.clock_drift_ms;
  const driftTone =
    drift === null
      ? 'muted'
      : Math.abs(drift) >= freshness.thresholds.clock_drift_fail_ms
        ? 'danger'
        : Math.abs(drift) >= freshness.thresholds.clock_drift_warn_ms
          ? 'warn'
          : 'ok';

  return (
    <div className="grid grid--3">
      <div className="kpi">
        <span className="kpi__label">Tick age</span>
        <span className="kpi__value text-mono">{formatAge(freshness.tick_age_ms)}</span>
        <StalenessBadge lastAt={freshness.last_tick_at} thresholds={{ aging: freshness.thresholds.tick_aging_ms, stale: freshness.thresholds.tick_stale_ms }} label="ticks" />
        <span className="kpi__sub">{tick.advice ?? 'Quote age is inside the configured staleness gate.'}</span>
      </div>
      <div className="kpi">
        <span className="kpi__label">Bar age</span>
        <span className="kpi__value text-mono">{formatAge(freshness.bar_age_ms)}</span>
        <StalenessBadge lastAt={freshness.last_bar_at} thresholds={{ aging: freshness.thresholds.bar_aging_ms, stale: freshness.thresholds.bar_stale_ms }} label="bars" />
        <span className="kpi__sub">{bar.advice ?? 'Bar aggregation is current for the configured cadence.'}</span>
      </div>
      <div className="kpi">
        <span className="kpi__label">Clock drift</span>
        <span className="kpi__value text-mono">{freshness.clock_drift_ms === null ? '—' : `${freshness.clock_drift_ms.toFixed(0)} ms`}</span>
        <span className={cx('badge', `tone-${driftTone}`)}>
          {driftTone === 'ok' ? 'within tolerance' : driftTone === 'warn' ? 'watch' : driftTone === 'danger' ? 'out of tolerance' : 'unknown'}
        </span>
        <span className="kpi__sub">
          warning ≥ {freshness.thresholds.clock_drift_warn_ms} ms, fail ≥ {freshness.thresholds.clock_drift_fail_ms} ms
        </span>
      </div>
    </div>
  );
}

/** Socket liveness, shown next to the data clock so a silent feed is obvious. */
export function SocketStatusBadge(): JSX.Element {
  const { socket, isMock } = useServices();
  const now = useNow(1_000);
  const silenceMs = socket.lastEventAt === null ? null : now - new Date(socket.lastEventAt).getTime();
  const silent = silenceMs !== null && silenceMs > SOCKET_POLICY.heartbeatTimeoutMs;
  const tone =
    socket.status === 'open' && !silent ? 'ok' : socket.status === 'connecting' || socket.status === 'reconnecting' ? 'warn' : 'danger';

  return (
    <span
      className={cx('badge', `tone-${tone}`)}
      title={
        isMock
          ? 'Synthetic event stream generated in the browser (src/mock/useMockSocket.ts). No daemon socket is open.'
          : socket.lastError ?? 'WebSocket to the Rust daemon'
      }
    >
      WS {isMock ? 'mock' : socket.status}
      {socket.status === 'open' && <> · {formatAge(silenceMs)}</>}
      {socket.missedEvents > 0 && <> · {socket.missedEvents} missed</>}
      {socket.attempts > 0 && socket.status === 'reconnecting' && <> · retry {socket.attempts}</>}
    </span>
  );
}
